import axios from 'axios';
import { logger } from '../../utils/logger';
import { api } from './base';

export const referenceDocumentsApi = {
  list: async (filters?: {
    documentType?: string;
    projectId?: string;
    indexStatus?: string;
    organizationId?: string;
    scope?: 'global' | 'company' | 'project' | 'all-projects' | 'assigned';  // Scope filter
  }) => {
    // Filter out undefined values before creating URLSearchParams
    const cleanFilters = Object.fromEntries(
      Object.entries(filters || {}).filter(([, v]) => v !== undefined)
    );
    const params = new URLSearchParams(cleanFilters as Record<string, string>);
    const response = await api.get(`/reference/documents?${params}`);
    return response.data.data;
  },

  get: async (id: string) => {
    const response = await api.get(`/reference/documents/${id}`);
    return response.data.data;
  },

  upload: async (metadata: {
    title: string;
    documentType: 'industry-standard' | 'advisory-circular' | 'guidance' | 'specification';
    description?: string;
    standardNumber?: string;
    revision?: string;  // Version identifier (e.g., "Rev A", "2.0") for version tracking
    revisionDate?: string;
    projectId?: string;
    customerId?: string;  // Customer ID for multi-tenant isolation
    tags?: string[];
    visibility?: 'organization' | 'project-specific';
    scope?: 'global' | 'company' | 'project';  // Document scope (global, company, project)
    filename?: string;  // Original filename for format detection (DOCX, XLSX, TXT, PDF)
    assignedProjects?: string[];  // Project assignments for scoping
  }) => {
    const response = await api.post('/reference/upload', metadata);
    return response.data.data;
  },

  uploadFile: async (uploadUrl: string, file: File, contentType: string) => {
    // Direct upload to S3 presigned URL (no auth header)
    await axios.put(uploadUrl, file, {
      headers: {
        'Content-Type': contentType
      }
    });
  },

  // Legacy alias for PDF uploads
  uploadPdf: async (uploadUrl: string, file: File) => {
    // Direct upload to S3 presigned URL (no auth header)
    await axios.put(uploadUrl, file, {
      headers: {
        'Content-Type': 'application/pdf'
      }
    });
  },

  delete: async (id: string) => {
    const response = await api.delete(`/reference/documents/${id}`);
    return response.data.data;
  },

  update: async (id: string, updates: Partial<{
    title: string;
    description: string;
    standardNumber: string;
    revisionDate: string;
    tags: string[];
    visibility: string;
  }>) => {
    const response = await api.put(`/reference/documents/${id}`, updates);
    return response.data.data;
  },

  getStatus: async (id: string) => {
    const response = await api.get(`/reference/documents/${id}/status`);
    return response.data.data;
  },

  reindex: async (id: string) => {
    const response = await api.post(`/reference/documents/${id}/reindex`);
    return response.data.data;
  },

  processWithPassword: async (documentId: string, pdfPassword: string) => {
    const response = await api.post(`/reference/documents/${documentId}/process`, { pdfPassword });
    return response.data.data;
  },

  getMarkdown: async (id: string) => {
    const response = await api.get(`/reference/markdown/${id}`);
    return response.data.data;
  },

  search: async (query: string, filters?: {
    projectId?: string;
    documentTypes?: string[];
    standards?: string[];
    limit?: number;
  }) => {
    const response = await api.post('/reference/search', { query, ...filters });
    return response.data.data;
  },

  // Project linking
  linkToProject: async (projectId: string, documentId: string) => {
    const response = await api.post(`/projects/${projectId}/link-reference`, { documentId });
    return response.data.data;
  },

  unlinkFromProject: async (projectId: string, documentId: string) => {
    const response = await api.delete(`/projects/${projectId}/unlink-reference/${documentId}`);
    return response.data.data;
  },

  getProjectReferences: async (projectId: string) => {
    const response = await api.get(`/projects/${projectId}/references`);
    return response.data.data;
  },

  getPdfUrl: async (documentId: string) => {
    const response = await api.get(`/reference/${documentId}/pdf-url`);
    return response.data.data.url;
  },
  
  // Promote document to Reference Library (All Projects scope)
  // Only admins can perform this action
  promote: async (documentId: string) => {
    const response = await api.post(`/reference/documents/${documentId}/promote`);
    return response.data.data;
  },

  // Demote document from Reference Library back to project-specific
  // Only admins can perform this action
  demote: async (documentId: string, options: { restorePrevious?: boolean; assignedProjects?: string[] }) => {
    const response = await api.post(`/reference/documents/${documentId}/demote`, options);
    return response.data.data;
  },

  // Share document to additional projects (cross-project sharing)
  share: async (documentId: string, targetProjects: string[]) => {
    const response = await api.post(`/reference/documents/${documentId}/share`, { targetProjects });
    return response.data.data;
  },

  // Remove document sharing from specific projects
  unshare: async (documentId: string, projectsToRemove: string[]) => {
    const response = await api.post(`/reference/documents/${documentId}/unshare`, { projectsToRemove });
    return response.data.data;
  },
  
  // Note: extractMetadata was removed - the endpoint never existed in the backend.
  // Users should fill in metadata manually through the upload form.
};

// Company Documents API (Policies & Templates)
export const companyDocumentsApi = {
  list: async (filter?: { type?: 'policy' | 'template'; category?: string; status?: string }) => {
    const response = await api.get('/company-documents', { params: filter });
    return response.data.data;
  },

  get: async (documentId: string, includeContent = false) => {
    const response = await api.get(`/company-documents/${documentId}`, {
      params: { includeContent: includeContent.toString() }
    });
    return response.data.data;
  },

  loadAsJson: async (documentId: string) => {
    const response = await api.get(`/company-documents/${documentId}`, {
      params: { includeContent: 'true' }
    });
    const doc = response.data.data;
    
    // Debug logging for company document loading
    logger.debug('[companyDocumentsApi.loadAsJson] Document:', documentId);
    logger.debug('[companyDocumentsApi.loadAsJson] Response format:', doc.format);
    logger.debug('[companyDocumentsApi.loadAsJson] Has jsonDocument:', !!doc.jsonDocument);
    logger.debug('[companyDocumentsApi.loadAsJson] Current revision:', doc.currentRevision);
    logger.debug('[companyDocumentsApi.loadAsJson] Revisions:', doc.revisions?.map((r: { revision: string; s3Key: string }) => ({ rev: r.revision, s3Key: r.s3Key })));
    
    // Return jsonDocument if available, otherwise null
    return doc.jsonDocument || null;
  },

  create: async (data: {
    title: string;
    documentType: 'policy' | 'template';
    category: string;
    subcategory?: string;
    docType?: string;
    content: string;
  }) => {
    const response = await api.post('/company-documents', data);
    return response.data.data;
  },

  update: async (documentId: string, updates: Record<string, unknown>) => {
    const response = await api.patch(`/company-documents/${documentId}`, updates);
    return response.data.data;
  },

  createRevision: async (documentId: string, changeDescription: string, author: string) => {
    const response = await api.post(`/company-documents/${documentId}/revisions`, {
      changeDescription,
      author,
    });
    return response.data.data;
  },

  updateStatus: async (documentId: string, newStatus: string, approver?: string, comments?: string) => {
    const response = await api.post(`/company-documents/${documentId}/status`, {
      newStatus,
      approver,
      comments,
    });
    return response.data.data;
  },

  deleteRevision: async (documentId: string, revision: string) => {
    const response = await api.delete(`/company-documents/${documentId}/revisions/${revision}`);
    return response.data.data;
  },
};

/**
 * Unified Search API
 * Search across all document types
 */
export const searchApi = {
  /**
   * Search all documents: project docs, prior projects, company docs, standards
   */
  searchAllDocuments: async (query: string, limit = 10) => {
    const response = await api.post('/search/documents', { query, limit });
    return response.data.data;
  },
};
